import { z } from "zod";
import { US_STATES } from "@utils/usStates";

// The address part of the admin client forms, shared by the Edit Organization
// (C5) and Edit Location (C6) drawers: street, city, state and ZIP, with the
// country sent as read. Both APIs validate an address the same way
// (DomainConstraint.Address, UsStateCode, UsZipCode).

// Mirrors DomainConstraint.Address on the API, so a length the validator
// would refuse with a 400 is caught inline.
const MAX = {
    streetAddress: 256,
    city: 128,
} as const;

// The API's UsStateCode: the 50 states and DC, nothing else (DecisionLog §1.3).
export const isUsStateCode = (value: string): boolean =>
    US_STATES.some((usState) => usState.code === value);

export const requiredText = (label: string, max: number) =>
    z
        .string()
        .trim()
        .nonempty(`${label} is required`)
        .max(max, `${label} must be ${max} characters or fewer`);

export const addressFormFields = {
    streetAddress: requiredText("Street address", MAX.streetAddress),
    city: requiredText("City", MAX.city),
    // The select emits only codes; a stored value it does not list is shown
    // by name and must be changed before saving (RestrictionFormDialog's rule).
    state: z.string().refine(isUsStateCode, "Choose a state from the list."),
    // The apply form's ZIP rule: 5 digits, optionally ZIP+4.
    zipCode: z
        .string()
        .trim()
        .nonempty("ZIP code is required")
        .regex(/^\d{5}(-\d{4})?$/, "Enter a 5-digit ZIP, optionally ZIP+4"),
};

export interface AddressFormValues {
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
}

// The state as the API stores and compares it: trimmed, upper case.
export const canonicalState = (state: string): string =>
    state.trim().toUpperCase();

// The marketplace is US-only (the state is one of the 50 + DC), so an address
// stored with a blank country is saved as US rather than refused with a 400
// the admin has no field to fix (Lead call 2026-10-28). A country that is not
// blank is sent back exactly as read.
const DEFAULT_COUNTRY = "US";

export const countryToSend = (readCountry: string): string =>
    readCountry.trim() ? readCountry : DEFAULT_COUNTRY;

// One line for an address on screen: its parts, blanks left out.
export const formatAddress = (address: {
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
}): string =>
    [
        address.streetAddress,
        address.city,
        address.state,
        address.zipCode,
        address.country,
    ]
        .filter(Boolean)
        .join(", ");
